import React, { useState, useEffect } from "react";
import { Button } from "@/components/fun-ui/Button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/fun-ui/Popover";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface YearPickerProps {
  value?: number; // 受控模式
  defaultValue?: number; // 非受控模式
  onValueChange?: (year: number) => void; // 受控模式
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const START_YEAR = 1800;

export const YearPicker = ({
  value: propValue,
  defaultValue,
  onValueChange,
  placeholder = '',
  disabled = false,
  className,
}: YearPickerProps) => {
  const [internalValue, setInternalValue] = useState(defaultValue || null); // 非受控模式内部状态
  const [startYear, setStartYear] = useState(START_YEAR); // 起始年份
  const [isOpen, setIsOpen] = useState(false); // 控制弹出层显示

  // 当前选中的年份（受控模式优先）
  const value = propValue !== undefined ? propValue : internalValue;
  const currentYear = new Date().getFullYear();

  // 当弹出层打开且value为空时，定位到包含当前年份的页面
  useEffect(() => {
    if (isOpen && !propValue) {
      const pageStart = Math.floor((currentYear - START_YEAR) / 20) * 20 + START_YEAR;
      setStartYear(pageStart);
    }
  }, [isOpen, propValue, currentYear]);

  // 初始化时，确保选中的年份在可见范围内
  useEffect(() => {
    if (value && value >= START_YEAR && value <= currentYear) {
      const pageStart = Math.floor((value - START_YEAR) / 20) * 20 + START_YEAR;
      setStartYear(pageStart);
    }
  }, [value, currentYear]);

  // 生成当前页的年份范围
  const generateYears = () => {
    const years = [];
    for (let i = 0; i < 20; i++) {
      const year = startYear + i;
      years.push(year);
    }
    return years;
  };

  // 处理年份选择
  const handleYearClick = (year: number) => {
    if (year <= currentYear && year >= START_YEAR) {
      if (propValue !== undefined) {
        // 受控模式
        onValueChange?.(year);
      } else {
        // 非受控模式
        setInternalValue(year);
        onValueChange?.(year);
      }
      setIsOpen(false); // 选择后关闭弹出层
    }
  };

  // 上一页
  const handlePrevious = () => {
    if (startYear - 20 >= START_YEAR) {
      setStartYear(startYear - 20);
    }
  };

  // 下一页
  const handleNext = () => {
    if (startYear + 20 <= currentYear) {
      setStartYear(startYear + 20);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start w-full focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 border border-input hover:bg-background",
            className
          )}
        >
          {value || <span className="text-muted-foreground">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        onClick={(e) => {
          e.stopPropagation();
        }}
        data-popover-content
        className="w-auto p-0 select-none "
        align="start"
      >
        <div className="p-4 bg-white rounded-lg shadow-md w-64">
          {/* 分页控制 */}
          <div className="flex justify-between items-center mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevious}
              disabled={startYear - 20 < START_YEAR}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">
              {startYear} - {Math.min(startYear + 19, currentYear)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              disabled={startYear + 20 > currentYear}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* 年份网格 */}
          <div className="grid grid-cols-4 gap-2">
            {generateYears().map((year) => (
              <Button
                key={year}
                variant={value === year ? "default" : "ghost"}
                size="sm"
                onClick={() => handleYearClick(year)}
                disabled={year > currentYear || year < START_YEAR}
                className="text-center"
              >
                {year}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
